/*
 * AI Inbox page
 * Purpose: Top-level operational queue for API-backed AI Inbox items with list selection and detail triage.
 * Implementation notes: Keeps the detail pane inside the page instead of using the global chat/editor right panel.
 * Recent changes: Added entity links for linked accounts and contacts in the detail metadata.
 * Recent changes: Uses the shared page layout on the routed page while preserving embedded panel padding.
 * Recent changes: Requires a valid due date before creating a task from an inbox item.
 * Recent changes: Derives new task titles from Description line one, matching note entry.
 * Recent changes: Adds the default-on assignment-email choice to AI Inbox task creation and displays backend queue outcomes.
 * Recent changes: Aligns the assignment-email choice left of task-dialog footer actions.
 * Recent changes: Uses the shared responsive task-popup layout and joined title-and-notes control.
 */

import { FC, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowPathIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  ExclamationCircleIcon,
  InboxIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'react-toastify';
import { THEME_CLASSES } from '../constants/themeConstants';
import {
  INBOX_ACTION_CATEGORIES,
  INBOX_ACTION_CATEGORY_LABELS,
  InboxActionCategory,
  InboxItem,
  INBOX_STATUS_LABELS,
  INBOX_STATUSES,
  InboxStatus
} from '../models/InboxItem';
import { getInboxItems, updateInboxStatus } from '../services/inboxService';
import { createTask } from '../services/taskService';
import { showTaskNotificationOutcome } from '../components/task/taskNotificationToast';
import Spinner from '../components/common/Spinner';
import { Button, Chip, DialogShell, Input } from '../components/primitives';
import {
  TaskDueDateField,
  TaskPopupFooter,
  TaskPopupLayout,
  TaskPriorityControl,
  TaskTextFields,
  TASK_POPUP_CLOSE_CLASS_NAME,
  TASK_POPUP_HEADER_CLASS_NAME,
  TASK_POPUP_TITLE_CLASS_NAME,
  composeTaskDescription,
  splitTaskDescription,
} from '../components/task/popup';
import {
  TASK_DUE_DATE_PAST_ERROR,
  TASK_DUE_DATE_REQUIRED_ERROR,
  TASK_DERIVED_TITLE_MAX_LENGTH,
  TASK_DESCRIPTION_TITLE_REQUIRED_ERROR,
  TaskPriority,
  getTaskDueDateValidationError,
  getTaskTitleFromDescription,
  getTodayDateValue,
} from '../models/Task';
import { useTeams } from '../context/TeamContext';
import { useEventBus } from '../context/EventBusContext';
import { request } from '../services/dataService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { EVENTS } from '../constants/eventConstants';
import PageLayout from '../components/patterns/PageLayout';

type StatusFilter = 'all' | InboxStatus;
type AccountFilter = 'all' | string;
type ContactFilter = 'all' | string;
type CategoryFilter = 'all' | 'uncategorized' | InboxActionCategory;
type CompactPanel = 'actions' | 'detail';

interface AIInboxPanelProps {
  fixedAccountId?: number | null;
  fixedContactId?: number | null;
  showAccountFilter?: boolean;
  showContactFilter?: boolean;
  className?: string;
  inset?: boolean;
}

interface UserOption {
  id: number;
  name: string;
  email?: string;
}

const COMPACT_PANEL_ORDER: CompactPanel[] = ['actions', 'detail'];
const COMPACT_PANEL_INDEX: Record<CompactPanel, number> = {
  actions: 0,
  detail: 1
};
const COMPACT_PANEL_LABELS: Record<CompactPanel, string> = {
  actions: 'Actions',
  detail: 'Detail'
};
const COMPACT_SWIPE_THRESHOLD = 48;
const AI_INBOX_SPLIT_QUERY = '(min-width: 1280px)';
const STATUS_TONES: Record<InboxStatus, string> = {
  open: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-200',
  done: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  dismissed: 'bg-slate-200 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
  removed: 'bg-rose-100 text-rose-800 dark:bg-rose-950 dark:text-rose-200',
  superseded: 'bg-sky-100 text-sky-800 dark:bg-sky-950 dark:text-sky-200',
  no_longer_supported: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200'
};

const STATUS_FILTERS: Array<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'All statuses' },
  ...INBOX_STATUSES.map((status) => ({ value: status, label: INBOX_STATUS_LABELS[status] }))
];

const CATEGORY_FILTERS: Array<{ value: CategoryFilter; label: string }> = [
  { value: 'all', label: 'All categories' },
  ...INBOX_ACTION_CATEGORIES.map((category) => ({ value: category, label: INBOX_ACTION_CATEGORY_LABELS[category] })),
  { value: 'uncategorized', label: 'Uncategorized' }
];

const formatDate = (value: string | null) => {
  if (!value) {
    return 'Not set';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
};

const getTeamLabel = (item: InboxItem) => item.teamName || `Team ${item.teamId}`;
const getAccountLabel = (item: InboxItem) => item.accountName || (item.accountId ? `Account ${item.accountId}` : 'No account');
const getContactLabel = (item: InboxItem) => item.contactName || (item.contactId ? `Contact ${item.contactId}` : 'No contact');

const buildEntityOptions = (
  items: InboxItem[],
  idKey: 'accountId' | 'contactId',
  labelGetter: (item: InboxItem) => string
) => {
  const options = new Map<string, string>();
  items.forEach((item) => {
    const id = item[idKey];
    if (id !== null) {
      options.set(String(id), labelGetter(item));
    }
  });

  return Array.from(options.entries())
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const getSearchText = (item: InboxItem) => [
  item.actionTitle,
  item.displayActionText,
  item.actionText,
  item.actionCategoryLabel,
  item.status,
  item.teamName,
  item.accountName,
  item.contactName,
  item.sourceDate,
  item.firstSeenDate,
  item.lastSeenDate,
  item.sourcePath,
  item.sourceActionsReportPath,
  item.sourceSummaryPath,
  item.sourceMarkdownSection,
  item.traceMarkdown,
  item.insightText,
  item.tensionsText,
  item.memoryText,
  item.evidenceDate,
  item.removedDate,
  item.removalReason,
  item.actionKey,
  item.accountId,
  item.contactId,
  item.teamId
].filter(Boolean).join(' ').toLowerCase();

const HighlightedText: FC<{ value: string; query: string; className?: string }> = ({ value, query, className }) => {
  const normalizedQuery = query.trim().toLowerCase();
  if (!normalizedQuery) {
    return <span className={className}>{value}</span>;
  }

  const lowerValue = value.toLowerCase();
  const parts: Array<{ text: string; isMatch: boolean }> = [];
  let cursor = 0;
  let matchIndex = lowerValue.indexOf(normalizedQuery);

  while (matchIndex !== -1) {
    if (matchIndex > cursor) {
      parts.push({ text: value.slice(cursor, matchIndex), isMatch: false });
    }

    parts.push({ text: value.slice(matchIndex, matchIndex + normalizedQuery.length), isMatch: true });
    cursor = matchIndex + normalizedQuery.length;
    matchIndex = lowerValue.indexOf(normalizedQuery, cursor);
  }

  if (cursor < value.length) {
    parts.push({ text: value.slice(cursor), isMatch: false });
  }

  return (
    <span className={className}>
      {parts.map((part, index) => part.isMatch ? (
        <mark
          key={`${part.text}-${index}`}
          className="rounded bg-yellow-200 px-0.5 text-inherit dark:bg-yellow-700/70"
        >
          {part.text}
        </mark>
      ) : (
        <span key={`${part.text}-${index}`}>{part.text}</span>
      ))}
    </span>
  );
};

const DetailRow: FC<{ label: string; value: ReactNode }> = ({ label, value }) => (
  <div className="grid gap-1 sm:grid-cols-[9rem_minmax(0,1fr)] sm:gap-4">
    <dt className={`text-xs font-medium uppercase tracking-wide ${THEME_CLASSES.text.secondary}`}>{label}</dt>
    <dd className={`min-w-0 break-words text-sm ${THEME_CLASSES.text.primary}`}>
      {value === null || value === undefined || value === '' ? 'Not set' : value}
    </dd>
  </div>
);

const EntityDetailLink: FC<{ to: string; children: string }> = ({ to, children }) => (
  <Link
    to={to}
    className="font-medium text-blue-600 underline-offset-2 hover:underline dark:text-blue-300"
  >
    {children}
  </Link>
);

const DUPLICATE_TRACE_SECTION_TITLES = new Set(['insight', 'insights', 'tension', 'tensions', 'memory']);
const TRACE_WRAPPER_SECTION_TITLES = new Set(['trace', 'evidence', 'evidence trail']);

const normalizeMarkdownHeadingTitle = (value: string) =>
  value
    .replace(/[`*_~[\]()]/g, '')
    .replace(/:$/, '')
    .trim()
    .toLowerCase();

const isTraceWrapperSectionTitle = (value: string) =>
  TRACE_WRAPPER_SECTION_TITLES.has(value) || value.startsWith('evidence trail ');

const getEvidenceTrailMarkdown = (item: InboxItem) => {
  const traceMarkdown = item.traceMarkdown?.trim();
  if (!traceMarkdown) {
    return null;
  }

  const lines = traceMarkdown.split(/\r?\n/);
  const evidenceLines: string[] = [];
  let isSkippingDuplicateSection = false;

  lines.forEach((line) => {
    const headingMatch = line.match(/^#{1,6}\s+(.+?)\s*#*\s*$/);
    if (headingMatch) {
      const headingTitle = normalizeMarkdownHeadingTitle(headingMatch[1]);
      isSkippingDuplicateSection = DUPLICATE_TRACE_SECTION_TITLES.has(headingTitle);

      if (isSkippingDuplicateSection || isTraceWrapperSectionTitle(headingTitle)) {
        return;
      }
    }

    if (!evidenceLines.length && isTraceWrapperSectionTitle(normalizeMarkdownHeadingTitle(line))) {
      return;
    }

    if (!isSkippingDuplicateSection) {
      evidenceLines.push(line);
    }
  });

  return evidenceLines.join('\n').replace(/\n{3,}/g, '\n\n').trim() || null;
};

const buildDetailMarkdown = (item: InboxItem) => [
  { title: 'Insight', value: item.insightText },
  { title: 'Tensions', value: item.tensionsText },
  { title: 'Memory', value: item.memoryText },
  { title: 'Evidence trail', value: getEvidenceTrailMarkdown(item) }
]
  .filter((section) => section.value)
  .map((section) => `### ${section.title}\n\n${section.value}`)
  .join('\n\n');

const buildTaskTitle = (item: InboxItem) => {
  const title = item.displayActionText.trim() || item.actionText.trim() || `AI Inbox item ${item.inboxId}`;
  return title.length > TASK_DERIVED_TITLE_MAX_LENGTH
    ? `${title.slice(0, TASK_DERIVED_TITLE_MAX_LENGTH - 3).trimEnd()}...`
    : title;
};

const buildTaskDescription = (item: InboxItem) => [
  buildTaskTitle(item),
  `Created from AI Inbox #${item.inboxId}.`,
  item.actionCategoryLabel ? `Category: ${item.actionCategoryLabel}` : null,
  item.actionText ? `Action instruction: ${item.actionText}` : null,
  item.insightText ? `Insight: ${item.insightText}` : null,
  item.tensionsText ? `Tensions: ${item.tensionsText}` : null,
  item.memoryText ? `Memory: ${item.memoryText}` : null,
  item.sourceDate ? `Source date: ${formatDate(item.sourceDate)}` : null,
  item.sourcePath ? `Source path: ${item.sourcePath}` : null,
  item.sourceActionsReportPath ? `Actions report: ${item.sourceActionsReportPath}` : null,
  item.actionKey ? `Action key: ${item.actionKey}` : null
].filter(Boolean).join('\n\n');

const buildNoteContent = (item: InboxItem) => [
  `Created from AI Inbox #${item.inboxId}.`,
  item.displayActionText || item.actionText ? `Action: ${item.displayActionText || item.actionText}` : null,
  item.actionText && item.actionText !== item.displayActionText ? `Action instruction: ${item.actionText}` : null,
  item.actionCategoryLabel ? `Category: ${item.actionCategoryLabel}` : null,
  item.accountName ? `Account: ${item.accountName}` : null,
  item.contactName ? `Contact: ${item.contactName}` : null,
  item.insightText ? `Insight: ${item.insightText}` : null,
  item.tensionsText ? `Tensions: ${item.tensionsText}` : null,
  item.memoryText ? `Memory: ${item.memoryText}` : null,
  item.sourceDate ? `Source date: ${formatDate(item.sourceDate)}` : null,
  item.sourcePath ? `Source path: ${item.sourcePath}` : null,
  item.sourceActionsReportPath ? `Actions report: ${item.sourceActionsReportPath}` : null,
  item.actionKey ? `Action key: ${item.actionKey}` : null
].filter(Boolean).join('\n\n');

export const AIInboxPanel: FC<AIInboxPanelProps> = ({
  fixedAccountId = null,
  fixedContactId = null,
  showAccountFilter,
  showContactFilter,
  className = '',
  inset = true
}) => {
  const { currentUserId } = useTeams();
  const { publish } = useEventBus();
  const [items, setItems] = useState<InboxItem[]>([]);
  const [selectedInboxId, setSelectedInboxId] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [accountFilter, setAccountFilter] = useState<AccountFilter>('all');
  const [contactFilter, setContactFilter] = useState<ContactFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  const [isTaskDialogOpen, setIsTaskDialogOpen] = useState(false);
  const [taskTitle, setTaskTitle] = useState('');
  const [taskNotes, setTaskNotes] = useState('');
  const [taskDueDate, setTaskDueDate] = useState('');
  const [taskPriority, setTaskPriority] = useState<TaskPriority>('Medium');
  const [sendTaskEmail, setSendTaskEmail] = useState(true);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedAssigneeIds, setSelectedAssigneeIds] = useState<number[]>([]);
  const [assigneeQuery, setAssigneeQuery] = useState('');
  const [isAssigneeFocused, setIsAssigneeFocused] = useState(false);
  const [activeAssigneeIndex, setActiveAssigneeIndex] = useState(-1);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [taskDialogError, setTaskDialogError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDesktopLayout, setIsDesktopLayout] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return true;
    }

    return window.matchMedia(AI_INBOX_SPLIT_QUERY).matches;
  });
  const [activeCompactPanel, setActiveCompactPanel] = useState<CompactPanel>('actions');
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const touchCurrentRef = useRef<{ x: number; y: number } | null>(null);
  const touchTargetBlockedRef = useRef(false);
  const assigneePickerRef = useRef<HTMLDivElement>(null);
  const isCompactLayout = !isDesktopLayout;
  const minTaskDueDate = useMemo(() => getTodayDateValue(), []);
  const shouldShowAccountFilter = showAccountFilter ?? fixedAccountId === null;
  const shouldShowContactFilter = showContactFilter ?? fixedContactId === null;
  const fixedInboxFilters = useMemo(() => {
    const filters: { accountId?: number; contactId?: number } = {};
    if (fixedAccountId !== null) {
      filters.accountId = fixedAccountId;
    }

    if (fixedContactId !== null) {
      filters.contactId = fixedContactId;
    }

    return filters;
  }, [fixedAccountId, fixedContactId]);
  const assigneeOptions = useMemo(() => {
    const optionsById = new Map<number, UserOption>();
    users.forEach((user) => {
      if (Number.isFinite(user.id) && user.id > 0) {
        optionsById.set(user.id, user);
      }
    });
    selectedAssigneeIds.forEach((assigneeId) => {
      if (!optionsById.has(assigneeId)) {
        optionsById.set(assigneeId, {
          id: assigneeId,
          name: `User #${assigneeId}`
        });
      }
    });

    return Array.from(optionsById.values());
  }, [selectedAssigneeIds, users]);
  const selectedAssignees = useMemo(() => {
    const optionsById = new Map(assigneeOptions.map((assignee) => [assignee.id, assignee]));
    return selectedAssigneeIds.map((assigneeId) => optionsById.get(assigneeId) ?? {
      id: assigneeId,
      name: `User #${assigneeId}`
    });
  }, [assigneeOptions, selectedAssigneeIds]);
  const selectedAssigneeLabel = selectedAssignees.length
    ? selectedAssignees.map((assignee) => assignee.name).join(', ')
    : 'Select assignees';
  const filteredAssignees = useMemo(() => {
    const trimmedQuery = assigneeQuery.trim().toLowerCase();
    if (!trimmedQuery) {
      return [];
    }

    const selectedIds = new Set(selectedAssigneeIds);
    return users
      .filter((user) => {
        if (selectedIds.has(user.id)) {
          return false;
        }

        return user.name.toLowerCase().includes(trimmedQuery)
          || user.email?.toLowerCase().includes(trimmedQuery);
      })
      .slice(0, 6);
  }, [assigneeQuery, selectedAssigneeIds, users]);
  const showAssigneeDropdown = isAssigneeFocused && assigneeQuery.trim().length > 0 && filteredAssignees.length > 0;

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) {
      return undefined;
    }

    const mediaQuery = window.matchMedia(AI_INBOX_SPLIT_QUERY);
    const handleMediaChange = (event: MediaQueryListEvent) => {
      setIsDesktopLayout(event.matches);
    };

    setIsDesktopLayout(mediaQuery.matches);
    mediaQuery.addEventListener('change', handleMediaChange);

    return () => {
      mediaQuery.removeEventListener('change', handleMediaChange);
    };
  }, []);

  const loadItems = useCallback(async () => {
    setIsLoading(true);

    try {
      const loadedItems = await getInboxItems(fixedInboxFilters);
      setItems(loadedItems);
      setSelectedInboxId((currentId) => {
        if (currentId && loadedItems.some((item) => item.inboxId === currentId)) {
          return currentId;
        }

        return loadedItems[0]?.inboxId ?? null;
      });
      setError(null);
    } catch (loadError) {
      console.error('Failed to load AI Inbox:', loadError);
      setItems([]);
      setSelectedInboxId(null);
      setError('Unable to load AI Inbox right now.');
    } finally {
      setIsLoading(false);
    }
  }, [fixedInboxFilters]);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  useEffect(() => {
    if (!shouldShowAccountFilter) {
      setAccountFilter('all');
    }

    if (!shouldShowContactFilter) {
      setContactFilter('all');
    }
  }, [shouldShowAccountFilter, shouldShowContactFilter]);

  useEffect(() => {
    if (!isTaskDialogOpen || users.length > 0) {
      return;
    }

    let isMounted = true;

    const loadUsers = async () => {
      setIsLoadingUsers(true);

      try {
        const usersResponse = await request(API_ENDPOINTS.GET_USERS);
        if (!isMounted) {
          return;
        }

        setUsers((Array.isArray(usersResponse) ? usersResponse : []).map((user) => ({
          id: user.id,
          name: user.name,
          email: user.email
        })));
      } catch (loadError) {
        if (!isMounted) {
          return;
        }

        console.error('Failed to load assignee options:', loadError);
        setTaskDialogError('Unable to load assignee options.');
      } finally {
        if (isMounted) {
          setIsLoadingUsers(false);
        }
      }
    };

    void loadUsers();

    return () => {
      isMounted = false;
    };
  }, [isTaskDialogOpen, users.length]);

  useEffect(() => {
    if (!showAssigneeDropdown) {
      setActiveAssigneeIndex(-1);
      return;
    }

    setActiveAssigneeIndex((previousIndex) => {
      if (previousIndex < 0 || previousIndex >= filteredAssignees.length) {
        return 0;
      }

      return previousIndex;
    });
  }, [filteredAssignees.length, showAssigneeDropdown]);

  useEffect(() => {
    if (!isTaskDialogOpen) {
      return undefined;
    }

    const handlePointerDown = (event: MouseEvent) => {
      if (!assigneePickerRef.current?.contains(event.target as Node)) {
        setIsAssigneeFocused(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    return () => document.removeEventListener('mousedown', handlePointerDown);
  }, [isTaskDialogOpen]);

  const visibleItems = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return items.filter((item) => {
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesAccount = accountFilter === 'all' || String(item.accountId) === accountFilter;
      const matchesContact = contactFilter === 'all' || String(item.contactId) === contactFilter;
      const matchesCategory = categoryFilter === 'all'
        || (categoryFilter === 'uncategorized' ? !item.actionCategory : item.actionCategory === categoryFilter);
      const matchesSearch = !normalizedQuery || getSearchText(item).includes(normalizedQuery);
      return matchesStatus && matchesAccount && matchesContact && matchesCategory && matchesSearch;
    });
  }, [items, searchQuery, statusFilter, accountFilter, contactFilter, categoryFilter]);

  const accountOptions = useMemo(() => buildEntityOptions(items, 'accountId', getAccountLabel), [items]);
  const contactOptions = useMemo(() => buildEntityOptions(items, 'contactId', getContactLabel), [items]);

  const selectedItem = useMemo(() => {
    if (selectedInboxId) {
      const selected = items.find((item) => item.inboxId === selectedInboxId);
      if (selected) {
        return selected;
      }
    }

    return visibleItems[0] ?? null;
  }, [items, selectedInboxId, visibleItems]);
  const selectedDetailMarkdown = useMemo(
    () => selectedItem ? buildDetailMarkdown(selectedItem) : '',
    [selectedItem]
  );
  useEffect(() => {
    if (selectedInboxId && visibleItems.some((item) => item.inboxId === selectedInboxId)) {
      return;
    }

    setSelectedInboxId(visibleItems[0]?.inboxId ?? null);
  }, [selectedInboxId, visibleItems]);

  const handleStatusChange = async (nextStatus: InboxStatus) => {
    if (!selectedItem || selectedItem.status === nextStatus) {
      return;
    }

    const previousItem = selectedItem;
    const previousStatusFilter = statusFilter;

    setIsUpdating(true);
    setItems((currentItems) => currentItems.map((item) => (
      item.inboxId === previousItem.inboxId ? { ...item, status: nextStatus } : item
    )));
    if (statusFilter !== 'all' && statusFilter !== nextStatus) {
      setStatusFilter(nextStatus);
    }

    try {
      const updatedItem = await updateInboxStatus(previousItem.inboxId, nextStatus);
      setItems((currentItems) => currentItems.map((item) => item.inboxId === updatedItem.inboxId ? updatedItem : item));
      setSelectedInboxId(updatedItem.inboxId);
      if (statusFilter !== 'all' && statusFilter !== updatedItem.status) {
        setStatusFilter(updatedItem.status);
      }
      setError(null);
    } catch (updateError) {
      console.error('Failed to update AI Inbox item:', updateError);
      setItems((currentItems) => currentItems.map((item) => (
        item.inboxId === previousItem.inboxId ? previousItem : item
      )));
      setStatusFilter(previousStatusFilter);
      setError('Unable to update this inbox item.');
    } finally {
      setIsUpdating(false);
    }
  };

  const openCreateTaskDialog = () => {
    if (!selectedItem) {
      return;
    }

    if (selectedItem.accountId === null) {
      setError('Unable to create a task because this inbox item is not tied to an account.');
      return;
    }

    const taskFields = splitTaskDescription(buildTaskDescription(selectedItem));
    setTaskTitle(taskFields.title);
    setTaskNotes(taskFields.notes);
    setTaskDueDate('');
    setTaskPriority('Medium');
    setSendTaskEmail(true);
    setSelectedAssigneeIds(currentUserId ? [currentUserId] : []);
    setAssigneeQuery('');
    setIsAssigneeFocused(false);
    setActiveAssigneeIndex(-1);
    setTaskDialogError(null);
    setIsTaskDialogOpen(true);
  };

  const closeCreateTaskDialog = () => {
    if (isCreatingTask) {
      return;
    }

    setIsTaskDialogOpen(false);
    setTaskDialogError(null);
  };

  const handleCreateTask = async () => {
    if (!selectedItem || selectedItem.accountId === null) {
      setTaskDialogError('Account is required.');
      return;
    }

    const normalizedTitle = getTaskTitleFromDescription(taskTitle);
    if (!normalizedTitle) {
      setTaskDialogError(TASK_DESCRIPTION_TITLE_REQUIRED_ERROR);
      return;
    }

    const dueDateValidationError = getTaskDueDateValidationError(taskDueDate, minTaskDueDate);
    if (dueDateValidationError) {
      setTaskDialogError(dueDateValidationError);
      return;
    }

    if (!selectedAssigneeIds.length) {
      setTaskDialogError('Select at least one assignee.');
      return;
    }

    setIsCreatingTask(true);
    setTaskDialogError(null);

    try {
      const creationResult = await createTask({
        title: normalizedTitle,
        description: composeTaskDescription(taskTitle, taskNotes),
        accountId: selectedItem.accountId,
        contactId: selectedItem.contactId,
        dueDate: taskDueDate || null,
        priority: taskPriority,
        sourceType: 'Manual',
        assigneeIds: selectedAssigneeIds,
        sendEmail: sendTaskEmail
      });
      setError(null);
      showTaskNotificationOutcome(creationResult.notification);
      toast.success(`Task "${creationResult.task.title}" created.`);
      setIsTaskDialogOpen(false);
    } catch (createError) {
      console.error('Failed to create task from AI Inbox item:', createError);
      setTaskDialogError('Unable to create a task from this inbox item.');
    } finally {
      setIsCreatingTask(false);
    }
  };

  const openCreateNoteDialog = () => {
    if (!selectedItem) {
      return;
    }

    if (selectedItem.accountId === null && selectedItem.contactId === null) {
      setError('Unable to create a note because this inbox item is not tied to an account or contact.');
      return;
    }

    setError(null);
    publish(EVENTS.OPEN_NOTE_MODAL, {
      accountId: selectedItem.accountId ?? undefined,
      contactId: selectedItem.contactId ?? undefined,
      teamId: selectedItem.teamId,
      initialContent: buildNoteContent(selectedItem)
    });
  };

  const handleAddAssignee = (assignee: UserOption) => {
    setSelectedAssigneeIds((previousIds) => previousIds.includes(assignee.id) ? previousIds : [...previousIds, assignee.id]);
    setAssigneeQuery('');
    setIsAssigneeFocused(false);
    setActiveAssigneeIndex(-1);
  };

  const handleRemoveAssignee = (assigneeId: number) => {
    setSelectedAssigneeIds((previousIds) => previousIds.filter((selectedId) => selectedId !== assigneeId));
  };

  const handleAssigneeKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (!showAssigneeDropdown) {
      if (event.key === 'Escape') {
        setIsAssigneeFocused(false);
        setActiveAssigneeIndex(-1);
      }
      return;
    }

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveAssigneeIndex((previousIndex) => Math.min(previousIndex + 1, filteredAssignees.length - 1));
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveAssigneeIndex((previousIndex) => Math.max(previousIndex - 1, 0));
      return;
    }

    if (event.key === 'Enter' && activeAssigneeIndex >= 0) {
      event.preventDefault();
      handleAddAssignee(filteredAssignees[activeAssigneeIndex]);
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      setIsAssigneeFocused(false);
      setActiveAssigneeIndex(-1);
    }
  };

  const hasRows = items.length > 0;
  const activeCompactPanelIndex = COMPACT_PANEL_INDEX[activeCompactPanel];
  const canMoveCompactPanelLeft = activeCompactPanelIndex > 0;
  const canMoveCompactPanelRight = activeCompactPanelIndex < COMPACT_PANEL_ORDER.length - 1;

  const goToCompactPanel = (panel: CompactPanel) => {
    setActiveCompactPanel(panel);
  };

  const moveCompactPanel = (direction: 'left' | 'right') => {
    setActiveCompactPanel((currentPanel) => {
      const currentIndex = COMPACT_PANEL_INDEX[currentPanel];
      const nextIndex = direction === 'left'
        ? Math.min(currentIndex + 1, COMPACT_PANEL_ORDER.length - 1)
        : Math.max(currentIndex - 1, 0);

      return COMPACT_PANEL_ORDER[nextIndex];
    });
  };

  const shouldIgnoreSwipeTarget = (target: EventTarget | null) => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }

    return Boolean(target.closest('input, textarea, select, button, a'));
  };

  const clearTouchTracking = () => {
    touchStartRef.current = null;
    touchCurrentRef.current = null;
    touchTargetBlockedRef.current = false;
  };

  const handleWorkspaceTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isDesktopLayout) {
      return;
    }

    touchTargetBlockedRef.current = shouldIgnoreSwipeTarget(event.target);
    if (touchTargetBlockedRef.current) {
      return;
    }

    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleWorkspaceTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (isDesktopLayout || touchTargetBlockedRef.current || !touchStartRef.current) {
      return;
    }

    const touch = event.touches[0];
    touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };
  };

  const handleWorkspaceTouchEnd = () => {
    if (isDesktopLayout || touchTargetBlockedRef.current || !touchStartRef.current || !touchCurrentRef.current) {
      clearTouchTracking();
      return;
    }

    const deltaX = touchCurrentRef.current.x - touchStartRef.current.x;
    const deltaY = touchCurrentRef.current.y - touchStartRef.current.y;

    clearTouchTracking();

    if (Math.abs(deltaX) < COMPACT_SWIPE_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) {
      return;
    }

    if (deltaX > 0) {
      moveCompactPanel('right');
      return;
    }

    moveCompactPanel('left');
  };

  const handleSelectInboxItem = (inboxId: number) => {
    setSelectedInboxId(inboxId);

    if (isCompactLayout) {
      setActiveCompactPanel('detail');
    }
  };

  const compactTrackStyle = isCompactLayout
    ? {
      width: `${COMPACT_PANEL_ORDER.length * 100}%`,
      transform: `translateX(-${COMPACT_PANEL_INDEX[activeCompactPanel] * (100 / COMPACT_PANEL_ORDER.length)}%)`,
      touchAction: 'pan-y'
    }
    : undefined;

  const compactPanelStyle = isCompactLayout
    ? {
      width: `${100 / COMPACT_PANEL_ORDER.length}%`
    }
    : undefined;

  const renderStatusControls = (variant: 'compact' | 'desktop') => {
    if (!selectedItem) {
      return null;
    }

    const isCompact = variant === 'compact';
    const createTaskTitle = selectedItem.accountId === null ? 'Account required to create task' : 'Create task';
    const canCreateNote = selectedItem.accountId !== null || selectedItem.contactId !== null;
    const createNoteTitle = canCreateNote ? 'Create note' : 'Account or contact required to create note';

    return (
      <div className={isCompact ? 'flex shrink-0 items-center gap-1.5' : 'flex shrink-0 items-center gap-2'}>
        <select
          aria-label="Selected item status"
          value={selectedItem.status}
          onChange={(event) => void handleStatusChange(event.target.value as InboxStatus)}
          disabled={isUpdating}
          className={`${isCompact ? 'h-8 w-[6.5rem] px-2 !text-xs' : 'h-9 w-32 px-2.5 !text-xs'} rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500 ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text}`}
        >
          {INBOX_STATUSES.map((status) => (
            <option key={status} value={status}>{INBOX_STATUS_LABELS[status]}</option>
          ))}
        </select>
        <button
          type="button"
          onClick={openCreateTaskDialog}
          disabled={isCreatingTask || selectedItem.accountId === null}
          className={`${isCompact ? 'h-8 w-8 p-0' : 'h-9 gap-1.5 px-3 !text-xs whitespace-nowrap'} inline-flex items-center justify-center rounded-md font-medium disabled:cursor-not-allowed disabled:opacity-50 ${THEME_CLASSES.button.primary}`}
          aria-label={isCompact ? 'Create task from selected inbox item' : undefined}
          title={createTaskTitle}
        >
          <DocumentPlusIcon className={isCompact ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
          {!isCompact && <span>{isCreatingTask ? 'Creating...' : 'Create Task'}</span>}
        </button>
        <button
          type="button"
          onClick={openCreateNoteDialog}
          disabled={!canCreateNote}
          className={`${isCompact ? 'h-8 w-8 p-0' : 'h-9 gap-1.5 px-3 !text-xs whitespace-nowrap'} inline-flex items-center justify-center rounded-md font-medium disabled:cursor-not-allowed disabled:opacity-50 ${THEME_CLASSES.button.secondary}`}
          aria-label={isCompact ? 'Create note from selected inbox item' : undefined}
          title={createNoteTitle}
        >
          <DocumentTextIcon className={isCompact ? 'h-4 w-4' : 'h-3.5 w-3.5'} />
          {!isCompact && <span>Create Note</span>}
        </button>
      </div>
    );
  };

  return (
    <div className={`flex h-full min-h-0 flex-col overflow-hidden ${inset ? 'p-4' : ''} ${className}`}>
      <div className="mb-4 overflow-x-auto pb-1">
        <div className="flex min-w-max flex-nowrap items-center gap-2">
          <select
            aria-label="Status filter"
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            className={`h-9 w-[9.5rem] shrink-0 rounded-md border px-2 !text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 xl:w-[8.75rem] ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text}`}
          >
            {STATUS_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          {shouldShowAccountFilter && (
            <select
              aria-label="Account filter"
              value={accountFilter}
              onChange={(event) => setAccountFilter(event.target.value)}
              className={`h-9 w-[10rem] shrink-0 rounded-md border px-2 !text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text}`}
            >
              <option value="all">All accounts</option>
              {accountOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          )}
          {shouldShowContactFilter && (
            <select
              aria-label="Contact filter"
              value={contactFilter}
              onChange={(event) => setContactFilter(event.target.value)}
              className={`h-9 w-[10rem] shrink-0 rounded-md border px-2 !text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text}`}
            >
              <option value="all">All contacts</option>
              {contactOptions.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          )}
          <select
            aria-label="Category filter"
            value={categoryFilter}
            onChange={(event) => setCategoryFilter(event.target.value as CategoryFilter)}
            className={`h-9 w-[10.5rem] shrink-0 rounded-md border px-2 !text-xs focus:outline-none focus:ring-2 focus:ring-blue-500 ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text}`}
          >
            {CATEGORY_FILTERS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void loadItems()}
            className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border text-sm font-medium ${THEME_CLASSES.border} ${THEME_CLASSES.text.primary} ${THEME_CLASSES.hover.background}`}
            disabled={isLoading}
            aria-label="Refresh AI Inbox"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800 dark:border-rose-900 dark:bg-rose-950 dark:text-rose-200">
          <ExclamationCircleIcon className="h-5 w-5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <div className={`relative flex shrink-0 items-center justify-between gap-3 rounded-t-lg border border-b-0 ${THEME_CLASSES.border} ${THEME_CLASSES.card.background} px-3 py-2 xl:hidden`}>
        <button
          type="button"
          onClick={() => canMoveCompactPanelLeft && goToCompactPanel(COMPACT_PANEL_ORDER[activeCompactPanelIndex - 1])}
          disabled={!canMoveCompactPanelLeft}
          className={`z-10 flex h-9 w-9 items-center justify-center rounded-full border text-sm transition-colors ${canMoveCompactPanelLeft
            ? `${THEME_CLASSES.border} ${THEME_CLASSES.text.primary} ${THEME_CLASSES.hover.background}`
            : 'cursor-not-allowed border-gray-200 text-gray-300 dark:border-gray-700 dark:text-gray-600'}`}
          aria-label="Show previous AI Inbox panel"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
            <path fillRule="evenodd" d="M11.78 4.22a.75.75 0 010 1.06L7.06 10l4.72 4.72a.75.75 0 11-1.06 1.06l-5.25-5.25a.75.75 0 010-1.06l5.25-5.25a.75.75 0 011.06 0z" clipRule="evenodd" />
          </svg>
        </button>

        <div className="pointer-events-none absolute left-1/2 top-1/2 flex min-w-0 -translate-x-1/2 -translate-y-1/2 flex-col items-center text-center">
          <div className="min-w-0 shrink-0">
            <div className={`text-sm font-semibold ${THEME_CLASSES.text.primary}`}>
              {COMPACT_PANEL_LABELS[activeCompactPanel]}
            </div>
            <div className="mt-1 flex items-center justify-center gap-1.5">
              {COMPACT_PANEL_ORDER.map((panel) => (
                <button
                  key={panel}
                  type="button"
                  onClick={() => goToCompactPanel(panel)}
                  className={`pointer-events-auto h-2.5 w-2.5 rounded-full transition-all ${activeCompactPanel === panel
                    ? 'bg-blue-600'
                    : 'bg-gray-300 dark:bg-gray-600'}`}
                  aria-label={`Show ${COMPACT_PANEL_LABELS[panel]} panel`}
                  aria-pressed={activeCompactPanel === panel}
                />
              ))}
            </div>
          </div>
        </div>

        {activeCompactPanel === 'detail' && (
          <div className="z-10 ml-auto">
            {renderStatusControls('compact')}
          </div>
        )}

        {canMoveCompactPanelRight && (
          <button
            type="button"
            onClick={() => goToCompactPanel(COMPACT_PANEL_ORDER[activeCompactPanelIndex + 1])}
            className={`z-10 flex h-9 w-9 items-center justify-center rounded-full border text-sm transition-colors ${THEME_CLASSES.border} ${THEME_CLASSES.text.primary} ${THEME_CLASSES.hover.background}`}
            aria-label="Show next AI Inbox panel"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="h-5 w-5" aria-hidden="true">
              <path fillRule="evenodd" d="M8.22 4.22a.75.75 0 011.06 0l5.25 5.25a.75.75 0 010 1.06l-5.25 5.25a.75.75 0 11-1.06-1.06L12.94 10 8.22 5.28a.75.75 0 010-1.06z" clipRule="evenodd" />
            </svg>
          </button>
        )}
      </div>

      <div className="sr-only xl:hidden" aria-live="polite">
        {COMPACT_PANEL_LABELS[activeCompactPanel]} panel selected
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <div
          data-testid="ai-inbox-panel-track"
          className="flex h-full min-h-0 transition-transform duration-300 ease-out xl:grid xl:grid-cols-[26rem_minmax(0,1fr)] xl:gap-4 xl:transition-none"
          onTouchStart={handleWorkspaceTouchStart}
          onTouchMove={handleWorkspaceTouchMove}
          onTouchEnd={handleWorkspaceTouchEnd}
          onTouchCancel={clearTouchTracking}
          style={compactTrackStyle}
        >
          <section
            className={`flex min-h-0 w-full shrink-0 flex-col overflow-hidden border ${THEME_CLASSES.border} ${THEME_CLASSES.card.background} xl:rounded-lg`}
            style={compactPanelStyle}
          >
          <div className={`flex flex-col gap-3 border-b ${THEME_CLASSES.border} px-4 py-3 sm:flex-row sm:items-center sm:justify-between`}>
            <p className={`text-sm font-medium ${THEME_CLASSES.text.primary}`}>{visibleItems.length} visible actions</p>
            <div className="relative min-w-0 sm:w-56">
              <MagnifyingGlassIcon className={`pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 ${THEME_CLASSES.text.secondary}`} />
              <input
                type="search"
                aria-label="Search AI Inbox"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search"
                className={`w-full rounded-md border py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${THEME_CLASSES.input.border} ${THEME_CLASSES.input.background} ${THEME_CLASSES.input.text} ${THEME_CLASSES.input.placeholder}`}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="flex h-full items-center justify-center p-6">
                <Spinner />
              </div>
            ) : !hasRows ? (
              <div className={`flex h-full flex-col items-center justify-center p-6 text-center ${THEME_CLASSES.text.secondary}`}>
                <InboxIcon className="mb-3 h-9 w-9" />
                <p className="text-sm">No AI Inbox items</p>
              </div>
            ) : visibleItems.length === 0 ? (
              <div className={`flex h-full items-center justify-center p-6 text-center text-sm ${THEME_CLASSES.text.secondary}`}>
                No items match the current filters.
              </div>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {visibleItems.map((item) => {
                  const isSelected = selectedItem?.inboxId === item.inboxId;
                  return (
                    <button
                      key={item.inboxId}
                      type="button"
                      aria-label={item.displayActionText}
                      onClick={() => handleSelectInboxItem(item.inboxId)}
                      className={`block w-full px-4 py-3 text-left transition-colors ${isSelected
                        ? 'bg-blue-50 dark:bg-blue-950/40'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                        }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_TONES[item.status]}`}>
                          {INBOX_STATUS_LABELS[item.status]}
                        </span>
                        <div className="ml-auto flex min-w-0 justify-end">
                          {item.actionCategoryLabel && (
                            <span className="max-w-full truncate rounded-full bg-indigo-100 px-2 py-0.5 text-right text-xs font-medium text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200">
                              {item.actionCategoryLabel}
                            </span>
                          )}
                        </div>
                      </div>
                      <p className={`mt-2 line-clamp-2 text-sm font-medium ${THEME_CLASSES.text.primary}`}>
                        <HighlightedText value={item.displayActionText} query={searchQuery} />
                      </p>
                      <div className={`mt-2 flex items-end justify-between gap-3 text-xs ${THEME_CLASSES.text.secondary}`}>
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="truncate">
                            <HighlightedText value={getTeamLabel(item)} query={searchQuery} />
                          </div>
                          <div className="truncate">
                            {item.accountId && <HighlightedText value={getAccountLabel(item)} query={searchQuery} />}
                            {item.accountId && item.contactId && <span> / </span>}
                            {item.contactId && <HighlightedText value={getContactLabel(item)} query={searchQuery} />}
                          </div>
                        </div>
                        <span className="shrink-0 text-right">{formatDate(item.sourceDate)}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section
          className={`min-h-0 w-full shrink-0 overflow-hidden border ${THEME_CLASSES.border} ${THEME_CLASSES.card.background} xl:rounded-lg`}
          style={compactPanelStyle}
        >
          {!selectedItem ? (
            <div className={`flex h-full min-h-[24rem] flex-col items-center justify-center p-8 text-center ${THEME_CLASSES.text.secondary}`}>
              <InboxIcon className="mb-3 h-10 w-10" />
              <p className="text-sm">Select an inbox item</p>
            </div>
          ) : (
            <div className="flex h-full min-h-0 flex-col">
              <div className={`border-b ${THEME_CLASSES.border} p-4 xl:p-5`}>
                <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between xl:gap-4">
                  <div className="min-w-0 xl:flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_TONES[selectedItem.status]}`}>
                        {INBOX_STATUS_LABELS[selectedItem.status]}
                      </span>
                      {selectedItem.actionCategoryLabel && (
                        <span className="rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-medium text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200">
                          {selectedItem.actionCategoryLabel}
                        </span>
                      )}
                      <span className={`text-xs ${THEME_CLASSES.text.secondary}`}>Inbox #{selectedItem.inboxId}</span>
                    </div>
                  </div>
                  {!isCompactLayout && renderStatusControls('desktop')}
                </div>
                <h2 className={`mt-3 text-base font-semibold leading-6 sm:text-lg sm:leading-7 xl:text-xl ${THEME_CLASSES.text.primary}`}>{selectedItem.displayActionText}</h2>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                {selectedDetailMarkdown && (
                  <section className={`prose prose-sm mb-6 max-w-none dark:prose-invert ${THEME_CLASSES.text.primary}`}>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {selectedDetailMarkdown}
                    </ReactMarkdown>
                  </section>
                )}

                <dl className={`grid gap-3 ${selectedDetailMarkdown ? `border-t ${THEME_CLASSES.border} pt-5` : ''}`}>
                  <DetailRow label="Team" value={getTeamLabel(selectedItem)} />
                  <DetailRow
                    label="Account"
                    value={selectedItem.accountId ? (
                      <EntityDetailLink to={`/accounts/${selectedItem.accountId}`}>
                        {getAccountLabel(selectedItem)}
                      </EntityDetailLink>
                    ) : null}
                  />
                  <DetailRow
                    label="Contact"
                    value={selectedItem.contactId ? (
                      <EntityDetailLink to={`/contacts/${selectedItem.contactId}`}>
                        {getContactLabel(selectedItem)}
                      </EntityDetailLink>
                    ) : null}
                  />
                  <DetailRow label="Category" value={selectedItem.actionCategoryLabel} />
                </dl>
              </div>
            </div>
          )}
        </section>
        </div>
      </div>
      <DialogShell
        open={isTaskDialogOpen}
        title="New task"
        onClose={closeCreateTaskDialog}
        panelClassName="!w-[calc(100vw-1.5rem)] !max-w-5xl !rounded-2xl sm:!w-[calc(100vw-3rem)]"
        headerClassName={TASK_POPUP_HEADER_CLASS_NAME}
        titleClassName={TASK_POPUP_TITLE_CLASS_NAME}
        closeButtonClassName={TASK_POPUP_CLOSE_CLASS_NAME}
        bodyPadding={false}
      >
        {selectedItem && (
          <div className="p-5 sm:p-6">
            <TaskPopupLayout
              primary={(
                <>
                  <div>
                    <div className={`mb-2 text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`}>Account or Contact</div>
                    <div className={`rounded-xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-4 py-3 text-sm`}>
                      <div className={`font-medium ${THEME_CLASSES.text.primary}`}>AI Inbox #{selectedItem.inboxId}</div>
                      <div className={`mt-1 ${THEME_CLASSES.text.secondary}`}>Account: {selectedItem.accountId ? getAccountLabel(selectedItem) : 'Account required'}</div>
                      <div className={`mt-1 ${THEME_CLASSES.text.secondary}`}>Contact: {selectedItem.contactId ? getContactLabel(selectedItem) : 'No contact'}</div>
                    </div>
                  </div>
                  <TaskTextFields
                    idPrefix="ai-inbox-task"
                    title={taskTitle}
                    notes={taskNotes}
                    onTitleChange={(value) => {
                      setTaskTitle(value);
                      if (taskDialogError === TASK_DESCRIPTION_TITLE_REQUIRED_ERROR && getTaskTitleFromDescription(value)) setTaskDialogError(null);
                    }}
                    onNotesChange={setTaskNotes}
                    invalid={taskDialogError === TASK_DESCRIPTION_TITLE_REQUIRED_ERROR}
                    notesRows={9}
                  />
                </>
              )}
              details={(
                <>
                  <TaskPriorityControl idPrefix="ai-inbox-task" value={taskPriority} onChange={setTaskPriority} />
                  <div>
                    <label className={`mb-2 block text-xs font-semibold uppercase tracking-[0.12em] ${THEME_CLASSES.text.secondary}`} htmlFor="ai-inbox-task-assignees">Assignees</label>
                    <div ref={assigneePickerRef} className="space-y-2">
                      {selectedAssignees.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedAssignees.map((assignee) => (
                            <Chip key={assignee.id} variant="dismissible">
                              <span>{assignee.name}</span>
                              <button type="button" onClick={() => handleRemoveAssignee(assignee.id)} className="ml-2 text-xs leading-none" aria-label={`Remove ${assignee.name}`}>x</button>
                            </Chip>
                          ))}
                        </div>
                      )}
                      <div className="relative">
                        <Input
                          id="ai-inbox-task-assignees"
                          value={assigneeQuery}
                          onChange={(event) => setAssigneeQuery(event.target.value)}
                          onFocus={() => setIsAssigneeFocused(true)}
                          onKeyDown={handleAssigneeKeyDown}
                          placeholder={selectedAssigneeLabel}
                          aria-expanded={showAssigneeDropdown}
                          aria-autocomplete="list"
                          aria-controls="ai-inbox-task-assignee-options"
                        />
                        {showAssigneeDropdown && (
                          <div className={`absolute z-20 mt-2 w-full rounded-xl border ${THEME_CLASSES.border} ${THEME_CLASSES.input.background} shadow-lg`}>
                            <ul id="ai-inbox-task-assignee-options" className="py-1" role="listbox">
                              {filteredAssignees.map((assignee, index) => (
                                <li key={assignee.id}>
                                  <button type="button" onClick={() => handleAddAssignee(assignee)} className={`flex w-full flex-col px-3 py-2 text-left text-sm ${THEME_CLASSES.text.primary} ${index === activeAssigneeIndex ? THEME_CLASSES.hover.background : ''} hover:bg-slate-100 dark:hover:bg-slate-800`} role="option" aria-selected={index === activeAssigneeIndex}>
                                    <span>{assignee.name}</span>
                                    {assignee.email && <span className={`text-xs ${THEME_CLASSES.text.secondary}`}>{assignee.email}</span>}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {isAssigneeFocused && assigneeQuery.trim() && !isLoadingUsers && filteredAssignees.length === 0 && <div className={`mt-2 rounded-xl border ${THEME_CLASSES.border} px-3 py-2 text-xs ${THEME_CLASSES.text.secondary}`}>No matching assignees.</div>}
                      </div>
                    </div>
                    {isLoadingUsers && <p className={`mt-1 text-xs ${THEME_CLASSES.text.secondary}`}>Loading assignees...</p>}
                  </div>
                  <TaskDueDateField
                    id="ai-inbox-task-due-date"
                    min={minTaskDueDate}
                    value={taskDueDate}
                    onChange={(value) => {
                      setTaskDueDate(value);
                      if ((taskDialogError === TASK_DUE_DATE_REQUIRED_ERROR || taskDialogError === TASK_DUE_DATE_PAST_ERROR) && !getTaskDueDateValidationError(value, minTaskDueDate)) setTaskDialogError(null);
                    }}
                    invalid={taskDialogError === TASK_DUE_DATE_REQUIRED_ERROR || taskDialogError === TASK_DUE_DATE_PAST_ERROR}
                  />
                </>
              )}
            />

            {taskDialogError && <div className="mt-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-300">{taskDialogError}</div>}

            <TaskPopupFooter
              testId="ai-inbox-task-actions"
              checked={sendTaskEmail}
              onCheckedChange={setSendTaskEmail}
              actions={(
                <>
                  <Button onClick={closeCreateTaskDialog} variant="secondary" size="sm" disabled={isCreatingTask}>Cancel</Button>
                  <Button onClick={() => void handleCreateTask()} size="sm" loading={isCreatingTask}>Create Task</Button>
                </>
              )}
            />
          </div>
        )}
      </DialogShell>
    </div>
  );
};

const AIInbox: FC = () => (
  <PageLayout className="px-4">
    <AIInboxPanel inset={false} />
  </PageLayout>
);

export default AIInbox;
